import { CreditBreakdownCreditType } from '@/@types/client-api';
import { View, Text, Link } from '@react-pdf/renderer';
import { ReactNode } from 'react';
import { getCreditsText } from '../util/CreditPlanHelpers';
import { Element, htmlToDOM, Text as HtmlText, type DOMNode } from 'html-react-parser';
import { colors } from '@/constants/tailwind';

export const getSafeCreditValue = (value?: number) =>
  Number.isFinite(value) ? Math.max(value ?? 0, 0) : 0;

export const renderInlineHtml = (nodes: DOMNode[]): ReactNode[] =>
  nodes.map((node, index) => {
    if (node instanceof HtmlText) return node.data;
    if (!(node instanceof Element) || ['script', 'style', 'template'].includes(node.name)) {
      return null;
    }

    const children = renderInlineHtml(node.children as DOMNode[]);

    if (node.name === 'a' && /^(https?:|mailto:|\/)/i.test(node.attribs.href ?? '')) {
      return (
        <Link
          key={index}
          href={node.attribs.href}
          style={{ color: '#040CDC', textDecoration: 'underline' }}
        >
          {children}
        </Link>
      );
    }

    if (node.name === 'br') return '\n';
    if (['strong', 'b'].includes(node.name)) {
      return (
        <Text key={index} style={{ fontWeight: 700 }}>
          {children}
        </Text>
      );
    }
    if (['em', 'i'].includes(node.name)) {
      return (
        <Text key={index} style={{ fontStyle: 'italic' }}>
          {children}
        </Text>
      );
    }

    return children;
  });

// Preserve CMS paragraph and link semantics using React PDF primitives instead of flattening HTML.
export const renderPdfHtml = (html: string): ReactNode[] => {
  const blocks = htmlToDOM(html).filter(node => !(node instanceof HtmlText) || !!node.data.trim());

  return blocks.map((block, index) => (
    <Text key={index} style={{ marginBottom: index < blocks.length - 1 ? 12 : 0 }}>
      {renderInlineHtml([block])}
    </Text>
  ));
};

// React PDF cannot render the DOM-based mini tracker, so reproduce its credit tree
// with PDF primitives while preserving the same complete/needed labels.
export const renderRequirements = (types: CreditBreakdownCreditType[], depth = 0): ReactNode => {
  return types.map((type, index) => {
    const subRequirements = type.subRequirements?.filter(child => child.isRequired) ?? [];
    const isLast = index == types.length - 1;
    const isSubRequirement = depth > 0;

    return (
      <View
        key={type.key ?? type.name ?? index}
        style={{
          marginLeft: depth * 18,
          paddingVertical: 16,
          borderBottom: !isLast ? '3px solid #ECECEC' : undefined
        }}
      >
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text
            style={{
              color: !isSubRequirement ? '#000' : '#53565A',
              fontSize: 14,
              fontWeight: 500,
              flexGrow: 1,
              flexShrink: 1,
              paddingRight: 12
            }}
          >
            {type.name ?? type.key}
          </Text>
          <Text
            style={{
              color: type.isComplete ? colors.serpentine.DEFAULT : '#53565A',
              fontSize: 14,
              flexShrink: 0
            }}
          >
            {type.isComplete
              ? 'Complete'
              : getCreditsText(
                  getSafeCreditValue(type.amount),
                  subRequirements.length > 0,
                  depth > 0
                )}
          </Text>
        </View>
        {!type.isAllOtherCredits && renderRequirements(subRequirements, depth + 1)}
      </View>
    );
  });
};
